/**
 * Scroll lock guard
 *
 * The EQUO accessibility widget (backend.get-equo.io) snapshots
 * document.body.className into localStorage['accessibilitySettings'].bodyClasses
 * and re-applies every one of those classes to <body> on each page load.
 *
 * If the snapshot happens while a theme modal is open — the header search, the
 * cart drawer, the mobile menu — it captures the theme's own scroll-lock class
 * (.overflow-hidden* is `overflow: hidden` in base.css). From then on the widget
 * re-adds that class on every page load and immediately re-snapshots the now
 * locked body, so the state sustains itself: the page cannot be scrolled, on
 * desktop and mobile, and it survives navigation and reload. Only the browser
 * that took the bad snapshot is affected, which is why it looks unreproducible.
 *
 * This runs synchronously in <head>, before the widget script, and does two
 * things: strips the lock classes out of the stored snapshot (which both heals
 * an already-poisoned browser and stops it being written back), and keeps a
 * watch on <body> for a lock class that no open UI accounts for.
 */
(function () {
  'use strict';

  var LOCK_CLASSES = [
    'overflow-hidden',
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop',
  ];

  var STORAGE_KEY = 'accessibilitySettings';

  /* ── 1. Clean the widget's stored snapshot ─────────────────────────── */

  function cleanStoredSnapshot() {
    var raw;
    try {
      raw = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      return; // storage blocked (private mode, blocked cookies) — nothing to clean
    }
    if (!raw) return;

    try {
      var settings = JSON.parse(raw);
      if (!settings || !Array.isArray(settings.bodyClasses)) return;

      var cleaned = settings.bodyClasses.filter(function (name) {
        return LOCK_CLASSES.indexOf(name) === -1;
      });
      if (cleaned.length === settings.bodyClasses.length) return;

      settings.bodyClasses = cleaned;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      /* malformed value — leave it for the widget to deal with */
    }
  }

  cleanStoredSnapshot();

  /* ── 2. Watch <body> for a lock nothing accounts for ───────────────── */

  /* The theme locks the page only while one of these is actually open. If a
     lock class is on <body> and none of them is, something else put it there. */
  function somethingIsOpen() {
    return !!(
      document.querySelector('details-modal details[open]') ||
      document.querySelector('modal-dialog[open]') ||
      document.querySelector('cart-drawer.active') ||
      document.querySelector('pickup-availability-drawer[open]') ||
      document.documentElement.classList.contains('mobile-menu-open')
    );
  }

  function dropOrphanLocks() {
    var body = document.body;
    if (!body) return;

    var held = LOCK_CLASSES.filter(function (name) {
      return body.classList.contains(name);
    });
    if (!held.length || somethingIsOpen()) return;

    held.forEach(function (name) {
      body.classList.remove(name);
    });
    cleanStoredSnapshot();
  }

  function watchBody() {
    dropOrphanLocks();
    new MutationObserver(dropOrphanLocks).observe(document.body, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  if (document.body) {
    watchBody();
  } else {
    document.addEventListener('DOMContentLoaded', watchBody);
  }

  // a bfcache restore replays the old DOM, stored snapshot included
  window.addEventListener('pageshow', function () {
    cleanStoredSnapshot();
    dropOrphanLocks();
  });
})();
